<div *ngIf="!declaration" class="error">
  <h1>No registration data provided</h1>
  <p>Please provide valid registration data in the URL query parameter.</p>
</div>

<div class="layout-error" role="alert">
  <h1>Registration page cannot fit on one US Letter page</h1>
  <p>
    The asset details, selected options, or configured overview notes exceed the available printable area. Review their
    length or contact your warranty provider before generating the registration. No registration PDF was created.
  </p>
</div>

<!-- The overview and buyer signature share one US Letter portrait page. -->
<div *ngIf="declaration as d" #overviewPage class="page overview-page">
  <div #overviewContent class="overview-content">
    <!-- Header -->
    <div class="header">
      <img
        *ngIf="d.logoUrl && !logoLoadError"
        [src]="d.logoUrl"
        alt="{{ providerName }} Logo"
        class="logo"
        (error)="onLogoError()"
      />
      <div class="header-content">
        <h1 class="title">{{ d.policyName }} Registration Page</h1>
        <p class="subtitle">{{ providerName }}</p>
      </div>
    </div>

    <!-- Agreement Number -->
    <div class="block">
      <div class="grid-6">
        <div class="label-cell">Contract Number</div>
        <div class="cell span-2">{{ d.contractNumber }}</div>
        <!-- Agreement Number -->
        <div class="label-cell">Agreement Number</div>
        <div class="cell span-2">{{ d.agreementNumber?.startsWith('ssn_') ? '' : d.agreementNumber || 'N/A' }}</div>

        <div class="label-cell">Coverage As Of</div>
        <div class="cell span-5">{{ today | date: 'MMM d, y, h:mm:ss a z' }}</div>
      </div>
    </div>

    <!-- Agreement Holder Information -->
    <div class="block">
      <div class="block-title">Agreement Holder Information</div>
      <div class="grid-6">
        <div class="label-cell">Name</div>
        <div class="cell span-2">{{ d.customerName }}</div>
        <div class="label-cell">Phone</div>
        <div class="cell span-2">{{ d.customerPhone || 'N/A' }}</div>

        <div class="label-cell">Address</div>
        <div class="cell span-5">
          {{ d.customerAddress?.line1
          }}<ng-container *ngIf="d.customerAddress?.line2">, {{ d.customerAddress.line2 }}</ng-container>
        </div>

        <div class="label-cell">City</div>
        <div class="cell">{{ d.customerAddress?.city }}</div>
        <div class="label-cell">State</div>
        <div class="cell">{{ d.customerAddress?.state }}</div>
        <div class="label-cell">Zip</div>
        <div class="cell">{{ d.customerAddress?.zip }}</div>
      </div>
    </div>

    <!-- Covered Asset -->
    <div class="block" *ngIf="d.coveredAsset as asset">
      <div class="block-title">Covered {{ asset.assetType }}</div>
      <ng-container *ngIf="asset.fields.length; else noAsset">
        <div class="asset-grid">
          <ng-container *ngFor="let field of asset.fields">
            <div class="label-cell">{{ field.label }}</div>
            <div class="cell" [innerHTML]="field.value"></div>
          </ng-container>
        </div>
      </ng-container>
      <ng-template #noAsset>
        <div class="grid-6">
          <div class="label-cell">Asset</div>
          <div class="cell span-5">N/A</div>
        </div>
      </ng-template>
    </div>

    <!-- Agreement Information -->
    <div class="block">
      <div class="block-title">Agreement Information</div>
      <div class="grid-6">
        <div class="label-cell">Agreement Term (months)</div>
        <div class="cell">{{ d.termInMonths }}</div>

        <div class="label-cell">Purchase Date</div>
        <div class="cell">{{ d.purchaseDate ? (d.purchaseDate | date: 'shortDate') : 'N/A' }}</div>

        <div class="label-cell">Effective Date</div>
        <div class="cell">{{ d.effectiveDate | date: 'shortDate' }}</div>

        <div class="label-cell">Renewal Date</div>
        <div class="cell">{{ d.renewalDate || 'N/A' }}</div>

        <div class="label-cell">Expiration Date</div>
        <div class="cell">{{ d.expirationDate ? (d.expirationDate | date: 'shortDate') : 'N/A' }}</div>

        <div class="label-cell">Deductible / Service Call Fee</div>
        <div class="cell">{{ d.deductible }}</div>

        <div class="label-cell">Agreement Purchase Price</div>
        <div class="cell">{{ d.purchasePrice | currency }}</div>
      </div>
    </div>

    <!-- Coverage Information -->
    <div class="block">
      <div class="block-title">Coverage Information</div>
      <div class="grid-6">
        <div class="label-cell">Base Plan</div>
        <div class="cell span-5">{{ d.policyName }} — {{ d.planName }}</div>

        <div class="label-cell">Options Selected</div>
        <div class="cell span-5">
          <ng-container *ngIf="d.selectedOptions && d.selectedOptions.length; else none">
            <div class="check-grid">
              <div class="check-item" *ngFor="let opt of d.selectedOptions"><span class="box">☑</span> {{ opt }}</div>
            </div>
          </ng-container>
          <ng-template #none>None</ng-template>
        </div>
      </div>
    </div>

    <!-- Seller Information -->
    <div class="block">
      <div class="block-title">Seller Information</div>
      <ng-container *ngIf="d.sellerName; else noSeller">
        <div class="grid-6">
          <div class="label-cell">Name</div>
          <div class="cell span-2">{{ d.sellerName }}</div>
          <div class="label-cell">Phone</div>
          <div class="cell span-2">{{ d.sellerPhone || 'N/A' }}</div>

          <div class="label-cell">Address</div>
          <div class="cell span-5">
            {{ d.sellerAddress?.line1
            }}<ng-container *ngIf="d.sellerAddress?.line2">, {{ d.sellerAddress.line2 }}</ng-container>
          </div>

          <div class="label-cell">City</div>
          <div class="cell">{{ d.sellerAddress?.city }}</div>
          <div class="label-cell">State</div>
          <div class="cell">{{ d.sellerAddress?.state }}</div>
          <div class="label-cell">Zip</div>
          <div class="cell">{{ d.sellerAddress?.zip }}</div>
        </div>
      </ng-container>
      <ng-template #noSeller>
        <div class="grid-6">
          <div class="label-cell">Seller</div>
          <div class="cell span-5">N/A</div>
        </div>
      </ng-template>
    </div>

    <!-- Handwritten signature only; no signature data is collected or stored. -->
    <div class="buyer-signature">
      <div class="signature-field">
        <div class="signature-line" aria-label="Space for handwritten buyer signature"></div>
        <div class="signature-label">Buyer Signature</div>
      </div>
      <div class="signature-date">
        <div class="signature-line" aria-label="Space for signature date"></div>
        <div class="signature-label">Date</div>
      </div>
    </div>

    <!-- Page 1 Footer -->
    <div class="page-footer" *ngIf="page1Footer">
      <p class="footer-text">{{ page1Footer }}</p>
    </div>
  </div>
</div>

<!-- Coverage begins on page two and can continue across any number of pages. -->
<div *ngIf="declaration as d" class="page summary-page" [class.landscape-page]="isSummaryLandscape">
  <div class="header summary-header">
    <div class="header-content">
      <h1 class="title">Coverage Summary</h1>
      <p class="subtitle">{{ providerName }}</p>
    </div>
  </div>

  <div class="block">
    <div class="block-title">Coverage Summary</div>
    <ng-container *ngIf="summaryPlans.length && summaryRows.length; else legacyCoverageTable">
      <table class="coverage-summary-table">
        <thead>
          <tr>
            <th class="item-column">Covered Item</th>
            <th *ngFor="let plan of summaryPlans" [class.selected-plan-header]="plan.isSelected">
              <span class="plan-name">{{ plan.name }}</span>
              <span class="plan-selected-badge" *ngIf="plan.isSelected">Your Plan</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <ng-container *ngFor="let row of summaryRows">
            <tr>
              <td class="coverage-name-cell">{{ row.coverageName }}</td>
              <td
                *ngFor="let value of row.values"
                [class.selected-plan-cell]="value.isSelected"
                [ngSwitch]="value.status"
              >
                <ng-container *ngSwitchCase="'included'">
                  <span *ngIf="value.liabilityLimit !== null">{{ value.liabilityLimit | currency }}</span>
                  <span *ngIf="value.liabilityLimit === null" class="status-included">✓</span>
                </ng-container>
                <ng-container *ngSwitchCase="'optional'">
                  <span *ngIf="value.liabilityLimit !== null" class="status-optional-amount">{{
                    value.liabilityLimit | currency
                  }}</span>
                  <span *ngIf="value.liabilityLimit === null" class="status-optional">{{
                    value.note || 'Optional'
                  }}</span>
                </ng-container>
                <ng-container *ngSwitchDefault>
                  <span *ngIf="value.liabilityLimit !== null" class="status-excluded-amount">{{
                    value.liabilityLimit | currency
                  }}</span>
                  <span *ngIf="value.liabilityLimit !== null" class="status-excluded-note">Not included</span>
                  <span *ngIf="value.liabilityLimit === null" class="status-excluded">—</span>
                </ng-container>
              </td>
            </tr>
          </ng-container>
        </tbody>
      </table>
    </ng-container>
    <ng-template #legacyCoverageTable>
      <table class="coverage-table">
        <thead>
          <tr>
            <th>Covered Products</th>
            <th>Covered under Service Plan</th>
            <th>Claim Limit of Liability per 12-month period for Product Less than 10 years Old</th>
            <th>Claim Limit of Liability per 12-month period for Product 10 years Old or Greater</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let c of d.coverages">
            <td>{{ c.coverageName }}</td>
            <td class="text-center">{{ c.coveredUnderServicePlan ? 'Yes' : 'No' }}</td>
            <td class="text-right">
              {{ c.liabilityLimitUnder10Years ? (c.liabilityLimitUnder10Years | currency) : 'N/A' }}
            </td>
            <td class="text-right">
              {{ c.liabilityLimit10YearsOrOlder ? (c.liabilityLimit10YearsOrOlder | currency) : 'N/A' }}
            </td>
          </tr>
        </tbody>
      </table>
    </ng-template>

    <div class="table-footer" *ngIf="coverageTableFooter">
      <p class="footer-note">{{ coverageTableFooter }}</p>
    </div>
  </div>

  <div class="page-footer" *ngIf="page2Footer">
    <p class="footer-text">{{ page2Footer }}</p>
  </div>
</div>
